import { US_ZIP_REGEX } from './validations';

export type GeocodedAddressComponent = {
  types: string[];
  long_name: string;
  short_name: string;
};

export type ParsedPastedAddress = {
  country: string;
  state: string;
  zip_code: string;
  city: string;
  address_line_one: string;
  address_line_two: string;
  name: string;
  phone: string;
};

const COMPONENTS_STRIPPED_FROM_PASTE = [
  'street_number',
  'route',
  'political',
  'sublocality',
  'postal_town',
  'sublocality_level_1',
  'subpremise',
  'country',
  'locality',
  'administrative_area_level_1',
  'administrative_area_level_2',
];

const ADDRESS_LINE_TWO_TYPES = [
  'political',
  'sublocality',
  'sublocality_level_1',
  'subpremise',
  'postal_town',
];

// Canada, e.g. "M5V 3L9". Separator is optional so "M5V3L9" normalises too.
const CA_POSTAL_REGEX = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/;
// Catch-all for the 5 and 6 digit numeric formats used outside the US and CA.
const INTL_NUMERIC_POSTAL_REGEX = /^\d{5,6}$/;

/**
 * Returns the canonical form of a candidate that is a postal code in its
 * entirety, or null when it is anything else. US codes are validated with the
 * same anchored regex the address forms use.
 */
function normalizePostalCode(candidate: string): string | null {
  const value = candidate.trim().replace(/^\(|\)$/g, '');

  if (US_ZIP_REGEX.test(value)) {
    return value;
  }
  if (CA_POSTAL_REGEX.test(value)) {
    return value.toUpperCase().replace(/[ -]?(\d[A-Za-z]\d)$/, ' $1');
  }
  if (INTL_NUMERIC_POSTAL_REGEX.test(value)) {
    return value;
  }
  return null;
}

/**
 * Pulls the postal code out of a single comma/newline separated chunk of a
 * pasted address, returning only the code itself and never the surrounding
 * text. The code has to be either the whole chunk or its tail, as in
 * "Mountain View CA 94043", and whatever precedes it must be free of digits so
 * street lines such as "12345 Sunset Boulevard" are not mistaken for one.
 */
export function extractPostalCode(token: string): string | null {
  const trimmed = (token || '').trim();
  if (!trimmed) {
    return null;
  }

  const wholeToken = normalizePostalCode(trimmed);
  if (wholeToken) {
    return wholeToken;
  }

  const words = trimmed.split(/\s+/);
  // Two words first, so Canadian codes written as "M5V 3L9" stay together.
  for (const tailLength of [2, 1]) {
    if (words.length <= tailLength) {
      continue;
    }
    const tail = normalizePostalCode(words.slice(-tailLength).join(' '));
    if (tail && !/\d/.test(words.slice(0, -tailLength).join(' '))) {
      return tail;
    }
  }

  return null;
}

function isPhoneNumber(inputString: string) {
  const pattern = /^(?=.*\d{7,})([+()]|\d)*$/;
  return pattern.test(inputString);
}

function hasNumber(inputString: string) {
  const pattern = /\d/;
  return pattern.test(inputString);
}

function canBeName(inputString: string) {
  const words = inputString.split(' ');
  if (words.length <= 2) {
    let wordsSafe = true;
    words.forEach(word => {
      wordsSafe = !hasNumber(word);
    });
    return wordsSafe;
  }
  return false;
}

function hasMoreThan1Word(inputString: string) {
  const words = inputString.split(' ');
  return words.length > 1;
}

/**
 * Turns the free-text the user pasted, plus whatever Google was able to
 * geocode from it, into the individual address fields of the manual order
 * form. Google's components win where they exist; the leftover text is
 * scavenged for the fields Google could not resolve.
 */
export function parsePastedAddress(
  address_components: GeocodedAddressComponent[],
  completeAddress: string,
): ParsedPastedAddress {
  let state = '';
  let zip_code = '';
  let g_zip_code = '';
  let country = '';
  let city = '';
  let address_line_two = '';
  let street_number = '';
  let route = '';
  let phone = '';
  let name = '';

  let remainingAddress = completeAddress;

  for (let i = 0; i < address_components.length; i += 1) {
    if (
      COMPONENTS_STRIPPED_FROM_PASTE.includes(address_components[i].types[0])
    ) {
      const charArr = [
        ...address_components[i].long_name.split(/[ -]/),
        ...address_components[i].short_name.split(/[ -]/),
      ];
      charArr.forEach(char => {
        const pattern = new RegExp(`\\b${char.toLowerCase()}\\b,?`, '');
        remainingAddress = remainingAddress
          .toLowerCase()
          .replace(pattern, match => {
            if (match.includes(',')) {
              return ',';
            }
            return '';
          });
      });
    }

    if (address_components[i].types[0] === 'street_number') {
      street_number = address_components[i].short_name;
    } else if (address_components[i].types[0] === 'route') {
      route = address_components[i].short_name;
    } else if (
      ADDRESS_LINE_TWO_TYPES.includes(address_components[i].types[0])
    ) {
      address_line_two = address_components[i].short_name;
    } else if (address_components[i].types[0] === 'country') {
      country = address_components[i].short_name;
    } else if (address_components[i].types[0] === 'postal_code') {
      g_zip_code = address_components[i].long_name;
    } else if (address_components[i].types[0] === 'locality') {
      city = address_components[i].long_name;
    } else if (
      address_components[i].types[0] === 'administrative_area_level_1'
    ) {
      state = address_components[i].long_name;
    }
  }

  if (!city) {
    for (let i = 0; i < address_components.length; i += 1) {
      if (address_components[i].types[0] === 'administrative_area_level_2') {
        city = address_components[i].long_name;
        break;
      }
    }
  }
  if (!state) {
    for (let i = 0; i < address_components.length; i += 1) {
      if (address_components[i].types[0] === 'administrative_area_level_2') {
        state = address_components[i].long_name;
        break;
      }
    }
  }

  let addressComps: string[] = [];
  addressComps = remainingAddress
    .split(/,|\n/)
    .map(x => x.replace(/,/g, '').trim())
    .filter(x => !!x);

  const lastWord = addressComps[addressComps.length - 1] || '';
  if (lastWord.length <= 3 && lastWord.includes(country.toLowerCase())) {
    addressComps = addressComps.filter(
      (_x, i) => i !== addressComps.length - 1,
    );
  }

  let findZipIndex = -1;
  for (let i = 0; i < addressComps.length; i += 1) {
    const candidate = extractPostalCode(addressComps[i]);
    if (candidate) {
      zip_code = candidate;
      findZipIndex = i;
      break;
    }
  }
  if (findZipIndex > -1) {
    addressComps = addressComps.filter((_x, i) => i !== findZipIndex);
  }
  if (!zip_code) {
    zip_code = g_zip_code;
    findZipIndex = addressComps.indexOf(g_zip_code);
    if (findZipIndex > -1) {
      addressComps = addressComps.filter((_x, i) => i !== findZipIndex);
    }
  }

  const indexOfPhone = addressComps.findIndex(x => isPhoneNumber(x));

  if (indexOfPhone > -1) {
    phone = addressComps[indexOfPhone];
    addressComps = addressComps.filter((_x, i) => i !== indexOfPhone);
  }

  if (addressComps.length > 1) {
    const findNameIndex = addressComps.findIndex(x => canBeName(x));
    if (findNameIndex > -1) {
      name = addressComps[findNameIndex];
      addressComps = addressComps.filter((_x, i) => i !== findNameIndex);
    }
  }

  let parsedAddressLine1 = '';

  if (!street_number && !route && addressComps.length > 0) {
    // no address_line_1
    const findAddressIndex = addressComps.findIndex(x => hasMoreThan1Word(x));
    if (findAddressIndex > -1) {
      parsedAddressLine1 = addressComps[findAddressIndex];
      addressComps = addressComps.filter((_x, i) => i !== findAddressIndex);
    } else {
      parsedAddressLine1 = addressComps[0];
      addressComps = addressComps.filter((_x, i) => i !== 0);
    }
  }

  if (addressComps.length > 0 && !name) {
    name = addressComps[0];
    addressComps = addressComps.filter((_x, i) => i !== 0);
  }

  if (!address_line_two && addressComps.length > 0) {
    address_line_two = addressComps[0];
    addressComps = addressComps.filter((_x, i) => i !== 0);
  }

  return {
    country,
    state,
    zip_code,
    city,
    address_line_one: `${street_number} ${route}`.trim() || parsedAddressLine1,
    address_line_two,
    name,
    phone,
  };
}
